import { cn } from "@breakout/ui/utils"
import { motion } from "motion/react"
import { memo, useCallback, useState } from "react"

type AvatarVariant = "circle" | "square"

const sizeClasses = {
	xs: "size-6",
	sm: "size-8",
	md: "size-10",
	lg: "size-14",
	xl: "size-20",
} as const

// Pixel values for explicit width/height (prevents layout shift)
const sizePixels = {
	xs: 24,
	sm: 32,
	md: 40,
	lg: 56,
	xl: 80,
} as const

// Size-proportionate rounding for square variant
const squareRounding = {
	xs: "rounded",
	sm: "rounded-md",
	md: "rounded-lg",
	lg: "rounded-lg",
	xl: "rounded-xl",
} as const

/**
 * Simple avatar component with fade-in animation.
 * Avatar URLs are always provided by the backend with fallbacks already applied.
 * Shows a placeholder background while loading to prevent invisible avatars.
 *
 * Uses loadedSrc pattern instead of boolean to avoid useEffect race conditions
 * where cached images load before the reset effect runs.
 */
export const SimpleAvatar = memo(function SimpleAvatar({
	src,
	alt = "",
	size = "md",
	className = "",
	border = false,
	hoverEffect = false,
	variant,
	style,
}: {
	src: string
	alt?: string
	size?: "xs" | "sm" | "md" | "lg" | "xl"
	className?: string
	border?: boolean
	hoverEffect?: boolean
	/** Shape variant: circle for user avatars, square for market avatars */
	variant: AvatarVariant
	/** Inline styles (e.g., for view-transition-name) */
	style?: React.CSSProperties
}) {
	// Track which src is loaded (not just whether something is loaded)
	// This avoids race conditions with useEffect resets
	const [loadedSrc, setLoadedSrc] = useState<string | null>(null)

	// Image is loaded when the loadedSrc matches current src
	const isLoaded = loadedSrc === src

	const handleLoad = useCallback(() => {
		setLoadedSrc(src)
	}, [src])

	const handleError = useCallback(() => {
		// Mark as "loaded" on error too so we show placeholder, not invisible image
		setLoadedSrc(src)
	}, [src])

	return (
		<div
			className={cn(
				"relative shrink-0 overflow-hidden bg-bg-tertiary",
				variant === "circle" ? "rounded-full" : squareRounding[size],
				sizeClasses[size],
				border && "border border-border-highlight",
				className,
			)}
			style={style}
		>
			<motion.img
				alt={alt}
				animate={{ opacity: isLoaded ? 1 : 0 }}
				className={cn(
					"absolute inset-0 size-full object-cover",
					hoverEffect && "transition-opacity hover:opacity-80",
				)}
				decoding="async"
				height={sizePixels[size]}
				initial={{ opacity: 0 }}
				onError={handleError}
				onLoad={handleLoad}
				referrerPolicy="no-referrer"
				src={src}
				transition={{ duration: 0.15 }}
				width={sizePixels[size]}
			/>
		</div>
	)
})
